import {
  ref,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
  type Ref,
} from 'vue';
import type { StoredScroll } from './useGridColumns';

const SAVE_DEBOUNCE_MS = 300;
const SETTLE_MS = 90;

interface GridScrollOptions {
  rowCount: Ref<number>;
  saveScroll: (scroll: StoredScroll) => void;
  initialScroll: StoredScroll | null;
  fastScrollSkeleton: Ref<boolean>;
  skeletonVelocity: Ref<number>;
}

// ── Scroll: horizontal header sync · fast-scroll skeleton · position restore ──
export function useGridScroll({
  rowCount,
  saveScroll,
  initialScroll,
  fastScrollSkeleton,
  skeletonVelocity,
}: GridScrollOptions) {
  const scrollElement = ref<HTMLElement | null>(null);
  const frameEl = ref<HTMLElement | null>(null);
  const isFastScrolling = ref(false);

  function syncHeaderFooterToScroll(): void {
    const element = scrollElement.value;
    const frame = frameEl.value;
    if (!element || !frame) {
      return;
    }
    frame.style.setProperty('--itp-grid-scroll-x', `${element.scrollLeft}px`);
    frame.style.setProperty(
      '--itp-grid-scrollbar-gutter',
      `${Math.max(0, element.offsetWidth - element.clientWidth)}px`,
    );
  }

  let previousScrollTop = 0;
  let previousTimestamp = 0;
  let settleTimer: ReturnType<typeof setTimeout> | undefined;
  let saveTimer: ReturnType<typeof setTimeout> | undefined;

  function handleScroll(event: Event): void {
    const element = event.target as HTMLElement;
    syncHeaderFooterToScroll();

    const scrollTop = element.scrollTop;
    const scrollLeft = element.scrollLeft;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(
      () => saveScroll({ top: scrollTop, left: scrollLeft }),
      SAVE_DEBOUNCE_MS,
    );

    if (!fastScrollSkeleton.value) {
      return;
    }
    const now = performance.now();
    const elapsedMs = now - previousTimestamp;

    if (previousTimestamp && elapsedMs > 0) {
      const distancePx = Math.abs(scrollTop - previousScrollTop);
      const velocityPxPerSecond = (distancePx / elapsedMs) * 1000;
      if (velocityPxPerSecond > skeletonVelocity.value) {
        isFastScrolling.value = true;
      }
    }

    previousScrollTop = scrollTop;
    previousTimestamp = now;

    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      isFastScrolling.value = false;
    }, SETTLE_MS);
  }

  let viewportResizeObserver: ResizeObserver | undefined;

  onMounted(() => {
    syncHeaderFooterToScroll();
    if (typeof ResizeObserver !== 'undefined' && scrollElement.value) {
      viewportResizeObserver = new ResizeObserver(syncHeaderFooterToScroll);
      viewportResizeObserver.observe(scrollElement.value);
    }
  });

  watch(rowCount, () => void nextTick(syncHeaderFooterToScroll), {
    immediate: true,
  });

  let restored = false;
  watch(
    rowCount,
    (count) => {
      if (restored || !initialScroll || count === 0) {
        return;
      }
      restored = true;
      void nextTick(() =>
        requestAnimationFrame(() => {
          const element = scrollElement.value;
          if (!element) {
            return;
          }
          if (initialScroll.left) {
            element.scrollLeft = initialScroll.left;
            syncHeaderFooterToScroll();
          }
          if (initialScroll.top) {
            element.scrollTop = initialScroll.top;
          }
        }),
      );
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    clearTimeout(settleTimer);
    clearTimeout(saveTimer);
    viewportResizeObserver?.disconnect();
  });

  return {
    scrollElement,
    frameEl,
    isFastScrolling,
    handleScroll,
    syncHeaderFooterToScroll,
  };
}
